// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fontConfig } from "@vanmeter/XDL/constants";
import { IBlogModel } from "@vanmeter/XDL/types";
import { color, Divider, ellipsis, formatDate, xParserOptions } from "@vanmeter/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    blog: IBlogModel;
    cardTheme?: {
        backgroundColor?: string;
        titleLinkColorVariant?: "primary" | "secondary" | "tertiary" | "quarternary";
        blogCategoriesLinkVariant?: "primary" | "secondary" | "tertiary" | "quarternary";
        summaryTextColor?: string;
    };
}

export interface BlogsCardStyles {
    cardWrapper?: InjectableCss;
    imageStyle?: LazyImageProps;
    cardInnerWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    dateTypography?: TypographyPresentationProps;
    categoryTypeLink?: LinkPresentationProps;
    titleStyle?: LinkPresentationProps;
    summaryTypography?: TypographyPresentationProps;
    readMoreLink?: LinkPresentationProps;
}

const styles: BlogsCardStyles = {
    cardWrapper: {
        css: css`
            border: 1px solid ${color("common.border")};
            border-radius: 4px;
            display: grid;
            grid-template-rows: subgrid;
            grid-row: span 5;
            gap: 0;

            ${({ backgroundColor }: any) =>
                backgroundColor &&
                css`
                    background-color: ${backgroundColor};
                `}
        `,
    },
    imageStyle: {
        css: css`
            width: 100%;
            aspect-ratio: 16 / 9;
            img {
                width: 100%;
                height: auto;
                object-fit: contain;
                aspect-ratio: 16 / 9;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            padding: 12px 12px 8px;
        `,
    },
    dateTypography: {
        ...fontConfig.xSmallSingleLine,
        css: css`
            white-space: nowrap;
        `,
    },
    categoryTypeLink: {
        mergeCss: true,
        typographyProps: {
            ...fontConfig.xSmallSingleLine,
            css: css`
                ${ellipsis(1)}
                word-break: break-all;
            `,
        },
        css: css`
            max-width: 97px;
        `,
    } as any,
    titleStyle: {
        typographyProps: {
            ...fontConfig.regularSingleLine,
            css: css`
                ${ellipsis(2)}
            `,
        },
        css: css`
            margin-bottom: 12px;
            padding: 0 12px;
            align-self: flex-start;
        `,
    },
    summaryTypography: {
        ...fontConfig.small,
        css: css`
            ${ellipsis(4)}
            padding: 0 12px;
            ${TypographyStyle} {
                font-size: ${fontConfig.small.size}px;
                line-height: ${fontConfig.small.lineHeight};
            }
        `,
    },
    readMoreLink: {
        typographyProps: {
            ...fontConfig.smallSingleLine,
        },
        css: css`
            padding: 16px 12px 12px;
        `,
    },
};

export const BlogInfoSection: FC<{
    blogDate?: string;
    blogType?: string;
    categories?: string[];
    linkVariant?: string;
    dateColor?: string;
}> = ({ blogDate, blogType, categories, linkVariant = "tertiary", dateColor }) => {
    const blogListPageUrl = useAppSelector(state => getPageLinkByPageType(state, "VmiBlogListPage")?.url);

    return (
        <StyledWrapper {...styles.infoWrapper} className="info-wrapper">
            {blogDate && (
                <Typography {...styles.dateTypography} color={dateColor}>
                    {formatDate(new Date(blogDate))}
                </Typography>
            )}
            {blogType && (
                <>
                    <Divider margin="0 8px" />
                    <Link
                        href={`${blogListPageUrl}?blogType=${blogType}`}
                        {...styles.categoryTypeLink}
                        {...{ variant: linkVariant }}
                        aria-label={blogType}
                    >
                        {blogType}
                    </Link>
                </>
            )}
            {categories && categories.length > 0 && (
                <>
                    <Divider margin="0 8px" />
                    <Link
                        href={`${blogListPageUrl}?category=${categories[0]}`}
                        {...styles.categoryTypeLink}
                        {...{ variant: linkVariant }}
                        aria-label={categories[0]}
                    >
                        {categories[0]}
                    </Link>
                </>
            )}
        </StyledWrapper>
    );
};

const BlogCard: FC<OwnProps> = ({ blog, cardTheme }) => {
    return (
        <StyledWrapper {...styles.cardWrapper} {...{ backgroundColor: cardTheme?.backgroundColor }}>
            <Clickable href={blog.url} aria-label={blog.title}>
                <LazyImage src={blog.image} altText={blog.imageAltText || blog.title} {...styles.imageStyle} />
            </Clickable>
            <BlogInfoSection
                blogDate={blog.date}
                blogType={blog.type}
                categories={blog.categories}
                linkVariant={cardTheme?.blogCategoriesLinkVariant}
            />
            <Link
                href={blog.url}
                {...styles.titleStyle}
                {...{ variant: cardTheme?.titleLinkColorVariant }}
                aria-label={blog.title}
            >
                {blog.title}
            </Link>
            <Typography {...styles.summaryTypography} color={cardTheme?.summaryTextColor}>
                {parse(blog.summary, xParserOptions)}
            </Typography>
            <Link href={blog.url} {...styles.readMoreLink} {...{ variant: cardTheme?.titleLinkColorVariant }}>
                {translate("Read More")}
                <VisuallyHidden>{` ${blog.title}`}</VisuallyHidden>
            </Link>
        </StyledWrapper>
    );
};

export default BlogCard;
